import axios from "axios";
import Cookies from "js-cookie";

export type ApiRecord = Record<string, unknown>;
export const socialLinkFields = [
  ["email", "Email"], ["google", "Google"], ["tiktok", "TikTok"],
  ["website", "Website"], ["facebook", "Facebook"], ["linkedin", "LinkedIn"],
  ["whatsapp", "WhatsApp"], ["instagram", "Instagram"], ["google_maps", "Google Maps"],
] as const;
export type SocialLinks = Record<typeof socialLinkFields[number][0], string>;

export interface PersonalInformation {
  name: string;
  email: string;
  mobile: string;
  mobileCountry: string;
  gdcNumber: string;
  nhsNumber: string;
  avatarUrl: string;
  role: string;
}

export interface AddressInformation {
  name: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  town: string;
  postcode: string;
  phone: string;
  socialLinks: SocialLinks;
}

export interface PracticeInformation extends AddressInformation {
  paymentPlanId: string;
}

export interface SiteInformation extends AddressInformation {
  id: string;
  displayName: string;
}

export function asRecord(value: unknown): ApiRecord {
  return value && typeof value === "object" && !Array.isArray(value) ? value as ApiRecord : {};
}

export function unwrap(value: unknown): unknown {
  return asRecord(value).data ?? value;
}

export function recordFrom(value: unknown, key: string): ApiRecord {
  const record = asRecord(unwrap(value));
  const nested = asRecord(record[key] ?? record);
  return key === "practice" && record.sites !== undefined ? { ...nested, sites: record.sites } : nested;
}

export function textValue(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

export function integerId(value: unknown): number | null {
  if (!/^\d+$/.test(textValue(value))) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

export function personalFrom(clinician: ApiRecord = {}, user: ApiRecord = {}): PersonalInformation {
  return {
    name: textValue(clinician.name) || [user.first_name, user.last_name].map(textValue).filter(Boolean).join(" "),
    email: textValue(clinician.email ?? user.email),
    mobile: textValue(clinician.mobile_phone),
    mobileCountry: textValue(clinician.mobile_phone_country),
    gdcNumber: textValue(clinician.gdc_number),
    nhsNumber: textValue(clinician.nhs_number),
    avatarUrl: textValue(clinician.image_url ?? user.image_url),
    role: textValue(clinician.role ?? user.role),
  };
}

export function addressFrom(record: ApiRecord = {}): AddressInformation {
  const links = asRecord(record.social_links);
  return {
    name: textValue(record.name), email: textValue(record.email),
    addressLine1: textValue(record.address_line_1), addressLine2: textValue(record.address_line_2),
    town: textValue(record.town), postcode: textValue(record.postcode), phone: textValue(record.contact_no),
    socialLinks: Object.fromEntries(socialLinkFields.map(([key]) => [key, textValue(links[key])])) as SocialLinks,
  };
}

export function practiceFrom(record: ApiRecord = {}): PracticeInformation {
  return { ...addressFrom(record), paymentPlanId: textValue(record.nhs_payment_plan_id) };
}

export function sitesFrom(record: ApiRecord): SiteInformation[] {
  const raw = unwrap(record.sites ?? []);
  return (Array.isArray(raw) ? raw : []).map((value) => {
    const site = asRecord(value);
    return { ...addressFrom(site), id: textValue(site.id), displayName: textValue(site.display_name) };
  });
}

export function requestHeaders() {
  const auth = Cookies.get("token") || axios.defaults.headers.common.Authorization
    || axios.defaults.headers.common.authorization;
  const token = textValue(auth).trim().replace(/^Bearer\s+/i, "").trim();
  if (!token || token === "undefined" || token === "null") {
    throw new Error("Your session has expired. Please sign in again.");
  }
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

export function onboardingUrl(endpoint: string) {
  const base = process.env.REACT_APP_BASE_URL_V2;
  if (!base) throw new Error("Profile updates are unavailable. Please try again later.");
  return `${base.replace(/\/+$/, "")}/onboarding/${endpoint}`;
}

export function personalPayload(personal: PersonalInformation, previousMobile: string) {
  const mobile = personal.mobile.trim().replace(/[\s()+-]/g, "");
  if (mobile && (!/^\d+$/.test(mobile) || !Number.isSafeInteger(Number(mobile)))) {
    throw new Error("Enter a valid mobile phone number using digits, spaces, +, - or parentheses.");
  }
  if (!mobile && previousMobile) throw new Error("Please enter a mobile phone number. The current number cannot be cleared.");
  return {
    name: personal.name.trim(), mobile_phone_country: personal.mobileCountry.trim(),
    gdc_number: personal.gdcNumber.trim(), nhs_number: personal.nhsNumber.trim(),
    ...(mobile ? { mobile_phone: Number(mobile) } : {}),
  };
}

export function addressPayload(value: AddressInformation, emailRequired: boolean) {
  if ([value.name, value.addressLine1, value.town, value.postcode, ...(emailRequired ? [value.email] : [])]
    .some((field) => !field.trim())) {
    throw new Error("Please complete all required fields.");
  }
  return {
    name: value.name.trim(), email: value.email.trim(), address_line_1: value.addressLine1.trim(),
    address_line_2: value.addressLine2.trim(), town: value.town.trim(), postcode: value.postcode.trim(),
    contact_no: value.phone.trim(),
    social_links: Object.fromEntries(socialLinkFields.map(([key]) => [key, value.socialLinks[key].trim()])),
  };
}

export function practicePayload(value: PracticeInformation, previousPaymentPlanId: string) {
  const paymentPlanId = value.paymentPlanId.trim();
  if (paymentPlanId && !integerId(paymentPlanId)) throw new Error("NHS payment plan ID must be a positive whole number.");
  if (!paymentPlanId && previousPaymentPlanId) throw new Error("Please enter an NHS payment plan ID. The current ID cannot be cleared.");
  return { ...addressPayload(value, true), ...(paymentPlanId ? { nhs_payment_plan_id: Number(paymentPlanId) } : {}) };
}

export function sitesPayload(sites: SiteInformation[]) {
  if (!sites.length) throw new Error("There are no sites to update.");
  const ids = sites.map((site) => site.id.trim());
  if (ids.some((id) => !id)) throw new Error("Each site needs an existing site ID.");
  if (new Set(ids).size !== ids.length) throw new Error("Each site must have a different ID.");
  return sites.map((site) => {
    if (!site.displayName.trim()) throw new Error("Please enter a display name for each site.");
    return { id: site.id.trim(), display_name: site.displayName.trim(), ...addressPayload(site, false) };
  });
}

export function errorMessage(error: unknown, action: string): string {
  if (axios.isAxiosError(error)) {
    const body = asRecord(error.response?.data);
    const detail = body.detail ?? body.message;
    const reason = typeof detail === "string" ? detail : Array.isArray(detail)
      ? detail.map((item) => {
        const field = asRecord(item);
        const location = Array.isArray(field.loc) ? field.loc.filter((part) => part !== "body").join(".") : "";
        return `${location ? `${location}: ` : ""}${textValue(field.msg) || "Invalid field"}`;
      }).join("; ")
      : error.response?.status === 500 ? "The server could not complete the update. Please try again later."
      : error.response?.status === 400 ? "The requested record was not found. Please reload the profile."
      : error.response?.status === 401 ? "Your session has expired. Please sign in again."
      : "Please try again.";
    return `Could not ${action}. ${reason}`;
  }
  return error instanceof Error ? error.message : `Could not ${action}. Please try again.`;
}
